import React from 'react';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import DynamicFormWidget from 'src/components/dynamic-widget/DynamicFormWidget';
import { DataService } from 'src/services/data-service';
import GenericDialog from 'src/components/GenericDialog';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { AnnouncementLevel, IAnnouncement, IUpdateAnnouncementModel } from '.';

interface IAddAnnouncementProps {
  onSuccess: () => void;
  onClose: () => void;
  announcement?: IAnnouncement | null;
}

const languages = {
  en: 'English',
  ar: 'Arabic',
  fr: 'French',
};

const levelItems: Record<string, string> = {
  [AnnouncementLevel.Info]: 'Info',
  [AnnouncementLevel.Advisory]: 'Advisory',
  [AnnouncementLevel.Important]: 'Important',
  [AnnouncementLevel.Critical]: 'Critical'
};


const AddAnnouncement: React.FC<IAddAnnouncementProps> = ({ onSuccess, onClose, announcement }) => {
  const dispatch = useAppDispatch();
  const isEditMode = !!announcement;

  const formConfig = React.useMemo((): Record<string, DynamicFieldTypes> => ({
    title: {
      type: DynamicField.TEXT,
      required: true,
      name: 'title',
      title: 'Title',
      value: announcement?.title || '',
      disabled: false
    },
    body: {
      type: DynamicField.TEXTAREA,
      required: true,
      name: 'body',
      title: 'Body',
      value: announcement?.body || '',
      disabled: false
    },
    language: {
      type: DynamicField.SELECT,
      required: true,
      name: 'language',
      title: 'Language',
      value: announcement?.language || '',
      disabled: false,
      items: languages
    },
    level: {
      type: DynamicField.SELECT,
      required: true,
      name: 'level',
      title: 'Level',
      value: announcement?.level || AnnouncementLevel.Info,
      disabled: false,
      items: levelItems
    }
  }), [announcement]);

  const submit = React.useCallback(async (data: Record<string, any>) => {
    const endpoint = isEditMode ? 'api/Announcement/update' : 'api/Announcement/create';
    const payload = isEditMode
      ? { id: announcement.id, ...data } as IUpdateAnnouncementModel
      : data;

    const response = await DataService.post(
      endpoint,
      payload,
      undefined
    );

    if (response.ok) {
      onSuccess();
      return true;
    } else {
      const result: { message: string } = await response.json();
      dispatch(showAlert({
        message: result.message || 'An error occurred!',
        type: 'error'
      }));
      return false;
    }
  }, [dispatch, onSuccess, isEditMode, announcement]);

  return (
    <GenericDialog
      open={true}
      title={isEditMode ? 'Edit Announcement' : 'Add Announcement'}
      onClose={onClose}
    >
      <DynamicFormWidget
        title=""
        fields={formConfig}
        onSubmit={submit}
      />
    </GenericDialog>
  );
};

export default AddAnnouncement;
